import React from 'react'
import { Button, Label, Tag, TextField, Dropdown } from '../index'
import { operators, versionRegex, generateId } from '../../Utils'
import './styles.css'

interface Operator {
    id: string,
    label: string,
    symbol: string
}

interface Version {
    operator: string,
    number: string,
    secondNumber: string,
    id: string,
    label: string
}

export default function VersionsEditor(): JSX.Element {
    const [versionList, setVersionList] = React.useState<Version[]>([{ operator: 'eq', number: '1.0.0', secondNumber: '', id: '1', label: '1.0.0' }])
    const [isEditing, setIsEditing] = React.useState<Boolean>(false)
    const operatorsList: any[] = Object.values(operators)
    const [error, setError] = React.useState(false)
    const defaultVersion: Version = { operator: 'eq', number: '', secondNumber: '', id: '', label: '' }
    const [currentVersion, setCurrentVersion] = React.useState<Version>(defaultVersion)

    const generateVersionLabel = (version: Version): string => {
        const operatorSymbol: Operator = operatorsList.find((el: Operator) => el.id === version.operator)
        return version.operator === operators.eq.id
            ? version.number
            : version.operator === operators.between.id
                ? operators.between.symbol.replace('{x}', version.number).replace('{y}', version.secondNumber)
                : `${operatorSymbol.symbol} ${version.number}`
    }

    const validateVersion = (): boolean => {
        let isValid = false
        if (currentVersion.operator === operators.between.id) {
            isValid = versionRegex.test(currentVersion.number) && versionRegex.test(currentVersion.secondNumber)
        } else {
            isValid = versionRegex.test(currentVersion.number)
        }
        setError(!isValid)
        return isValid;
    }

    const handleChangeVersion = (e: any) => {

        if (e.target.id === 'number') {
            setCurrentVersion((prev: Version) => ({ ...prev, number: e.target.value }))
        } else if (e.target.id === 'secondNumber') {
            setCurrentVersion((prev: Version) => ({ ...prev, secondNumber: e.target.value }))

        }
    }

    const handleOperatorSelect = (e: any) => {
        setCurrentVersion((prev: Version) => ({ ...prev, number: prev?.number, operator: e, id: prev.id }))
    }
    const handleDeleteTag = () => {
        const newList = versionList.filter(el => el.id !== currentVersion.id)
        setVersionList(newList)
        setCurrentVersion(defaultVersion)
    }
    const handleOnClickTag = (version: Version) => {
        setIsEditing(true)
        setCurrentVersion(version)
    }
    const handleEditTag = () => {
        if (validateVersion()) {
            const label = generateVersionLabel(currentVersion)
            const newList = versionList.map(el => el.id === currentVersion.id ? { ...currentVersion, label } : el)
            setVersionList(newList)
        }
    }

    const handleCreateVersion = () => {
        if (validateVersion()) {
            const label = generateVersionLabel(currentVersion)
            const newList = [...versionList, { ...currentVersion, label, id: generateId() }]
            setVersionList(newList)
            setCurrentVersion(defaultVersion)
        }
    }

    const handleCancel = () => {
        setCurrentVersion(defaultVersion)
        setIsEditing(false)
    }

    const handleAdd = () => {
        setIsEditing(true)
    }
    return (
        <div className='editor-wrapper'>
            <div className='actions-box container' id="actions">
                <div className='label-container'>
                    <Label text="Versions" />
                </div>
                <div className='buttons-wrapper'>
                    {
                        !isEditing ? (
                            <Button id="add-version-button" label="Add version" variant="outlined" onClick={handleAdd} />
                        ) : (
                            <>
                                <div className='add-save-button-wrapper'>
                                    {
                                        Boolean(currentVersion.id) ? (
                                            <Button label="Save" variant="outlined" onClick={handleEditTag} />
                                        ) : (
                                            <Button label="Add" variant="outlined" onClick={handleCreateVersion} />
                                        )
                                    }

                                </div>
                                <div>
                                    <Button label="Cancel" variant="outlined" onClick={handleCancel} />
                                </div>
                            </>
                        )
                    }
                </div>
            </div>

            <div className='tag-container container' role='list'>
                    {
                        versionList.map(ver => {
                            const parsedNumber = ver.number.split('.')
                            const environment = Number(parsedNumber[0]) > 0 && Number(parsedNumber[0]) >= 999 ? 'test' : 'prod'
                            return (
                                <Tag
                                    role='listitem'
                                    key={ver.id}
                                    value={ver.label}
                                    variant={environment}
                                    onClick={() => handleOnClickTag(ver)}
                                />
                            )
                        })
                    }
            </div>
            {
                isEditing ? (
                    <div className='inputs-container container'>
                        <div className='dropdown-container'>
                            <Dropdown
                                value={currentVersion.operator}
                                values={operatorsList}
                                onSelect={handleOperatorSelect}
                            />
                        </div>

                        <div className='textfield-container'>
                            <TextField
                                id='number'
                                value={currentVersion.number}
                                placeholder={currentVersion.operator === operators.between.id ? "Min" : 'Version'}
                                onChange={handleChangeVersion}
                            />
                        </div>
                        {
                            currentVersion.operator === operators.between.id && (
                                <div className='textfield-container'>
                                    <TextField
                                        id='secondNumber'
                                        value={currentVersion.secondNumber}
                                        placeholder="Max"
                                        onChange={handleChangeVersion}
                                    />
                                </div>
                            )
                        }
                        {
                            isEditing && Boolean(currentVersion.id) ? (
                                <div className='delete-button'>
                                    <Button label="X" variant="delete" onClick={handleDeleteTag} />
                                </div>
                            ) : <></>
                        }
                    </div>
                ) : <></>
            }
            {
                error && (
                    <div className='container'>
                        <p aria-live='polite' className='error-message'>Version must be formatted as [num].[num].[num]</p>
                    </div>
                )
            }
        </div>
    )
}